import React, { useCallback, useRef, useState } from 'react';
import { namesAndIds } from '../../../../../../../types/shared/PrequalificationDataEntry';
import './EmailInput.css';
import { useAutoFillFocus } from '../../../../../providers/AutoFillFocusProvider';
import { usePersonalInformationError } from '../../../../../providers/PersonalInformationErrorProvider';
import { usePersonalInformation } from '../../../../../providers/PersonalInformationProvider';
import { usePageProperties } from '../../../../../providers/PagePropertiesProvider';
import useBriteVerify from '../../../../../../../hooks/useBriteVerify';
import useActiveElement from '../../../../../../../hooks/useActiveElement';
import SessionStorage from '../../../../../../../utils/SessionStorage';
import EmailField, { TEmailValidationError } from '../../../../../../FieldContainers/EmailField/EmailField';
import { isValidEmailStatus } from '../../../../../../../utils/acquisition-utils';
import { TErrorType } from '../../../../../../../providers/Pushdown/util/getPushdownError';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { isValidEmailFormat } from '../../../../../../../utils/cob-utils';

export default function EmailInput() {
    const { emailMessage, author } = usePageProperties();
    const inputEmail = useRef<HTMLInputElement>(null);
    const showSuggestions = useRef<boolean>(false);
    const [showIcon, setShowIcon] = useState(false);
    const {
        personalInformation: { EmailAddress },
        setSingleValue,
    } = usePersonalInformation();
    const {
        personalInformationError: {
            EmailAddress: { isInError, required },
        },
        dispatch,
    } = usePersonalInformationError();
    const [focusedOnce, setFocusedOnce] = useState<boolean>(false);
    const { input, autoFillFocusOnBlurHandler, autoFillFocusOnFocusHandler } =
        useAutoFillFocus();

    const activeElement = useActiveElement();
    const setValue = useCallback(setSingleValue('EmailAddress'), []);

    const setValid = useCallback((valid: boolean, errorType?: TErrorType) => {
        dispatch({ type: 'setValid', target: 'EmailAddress', payload: valid, errorType });
    }, []);

    const { emailValidationApi } = useBriteVerify();
    const [elist] = useState<String[]>(
        PathUtils.getMetaPropertyValue('cq:elist')
            ?.split(',')
            .map(email => atob(email)) ?? [],
    );
    const emailAutofill = PathUtils.getMetaPropertyValue('cq:emailAutofill')?.split(',').map(email => ({ value: email })) ?? null;

    return (
        <div>
            <div className='Pqde-AutoCompleteEmail__container'>
                <div id='emailAddress-field-container'>
                    <EmailField
                        alternate
                        emailAutofill={emailAutofill}
                        className={`Pqde-AutoCompleteEmail ${input.isFocused &&
              showSuggestions.current &&
              !isValidEmailFormat(EmailAddress)
                            ? 'Pqde-AutoCompleteEmail__suggestionBorder'
                            : ''
                        }`}
                        ref={inputEmail}
                        {...namesAndIds.EmailAddress}
                        value={EmailAddress}
                        setValueCallback={setValue}
                        onFocusCallback={() => {
                            if (!focusedOnce) {
                                setFocusedOnce(true);
                            }
                            autoFillFocusOnFocusHandler();
                        }}
                        onBlurCallback={autoFillFocusOnBlurHandler}
                        validateEmail={async (email: string): Promise<TEmailValidationError> => {
                            const isAdobeMode = SessionStorage.contains('adobeMode');
                            const useEmailApi = SessionStorage.contains('useEmailApi');
                            if (elist.length > 0) {
                                if (
                                    elist.includes(
                                        email.toLowerCase().substring(
                                            email.indexOf('@') + 1,
                                        ),
                                    )
                                ) {
                                    return 'ThirdParty';
                                }
                            }
                            if (
                                (required && !author && !isAdobeMode) ||
                                (isAdobeMode && useEmailApi)
                            ) {
                                const isValid = await emailValidationApi(email);
                                if (isValid) {
                                    const isEmailValid = typeof isValid === 'string'
                                        ? true
                                        : !!isValid.email && isValidEmailStatus(isValid.email.status);
                                    return isEmailValid ? undefined : 'ThirdParty';
                                }
                                return 'ThirdParty';
                            }
                            return undefined;
                        }}
                        onValidationComplete={(_email: string, error: TEmailValidationError): void => {
                            setShowIcon(!error);
                            setValid(!error, error);
                        }}
                        showIcon={showIcon}
                        error={isInError && activeElement !== inputEmail.current}
                        useSuccessIcon
                        emailMessage={emailMessage}
                        emailClassName='PqDataEntryForm__main--disclaimer'
                    />
                </div>
            </div>
        </div>
    );
}
